import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  permissionCategories, type ManagedUser, type Permission, type PermissionGroup,
} from "@brasilnovo/shared";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { ErrorBox, PageHeader, Spinner } from "../components/ui.js";
import { Modal } from "../components/Modal.js";
import {
  SITUACAO_LABEL, alternarPermissao, limparAjustes, linkConvite, mensagemConvite,
  permissoesEfetivas, situacao, whatsappUrl, type Ajustes, type Situacao,
} from "../lib/usuarios.js";

// =====================================================================
// Usuários e permissões (D-27). Quem tem a permissão "usuarios" cria,
// edita e exclui contas, gera convites e ajusta permissões por GRUPO ou
// por PESSOA. Contas nascem sem senha: o convite é o único caminho.
// =====================================================================

const CATEGORIAS = permissionCategories();
const LABEL = Object.fromEntries(CATEGORIAS.flatMap((c) => c.items.map((i) => [i.key, i.label]))) as Record<string, string>;

const SITUACAO_COR: Record<Situacao, string> = {
  ativo: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  convite: "bg-gold-50 text-gold-900 ring-gold-200",
  aguardando: "bg-slate-50 text-slate-600 ring-slate-200",
  inativo: "bg-red-50 text-red-700 ring-red-200",
};

const dataHora = (v: string | null) => (v ? new Date(v).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");

interface InviteShown { user: { name: string; email: string }; token: string; expiresAt: string }

export function Admin() {
  const [aba, setAba] = useState<"usuarios" | "grupos">("usuarios");
  const [editando, setEditando] = useState<ManagedUser | "novo" | null>(null);
  const [grupoEditando, setGrupoEditando] = useState<PermissionGroup | "novo" | null>(null);
  const [convite, setConvite] = useState<InviteShown | null>(null);
  const [excluindo, setExcluindo] = useState<ManagedUser | null>(null);

  const users = useQuery({ queryKey: ["admin-users"], queryFn: async () => (await api.get("/admin/users")).data as ManagedUser[] });
  const groups = useQuery({ queryKey: ["admin-groups"], queryFn: async () => (await api.get("/admin/groups")).data as PermissionGroup[] });

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Usuários e permissões"
        subtitle="Quem acessa o portal e o que cada pessoa pode fazer"
        action={aba === "usuarios"
          ? <button className="btn-primary" data-testid="novo-usuario" onClick={() => setEditando("novo")}>+ Novo usuário</button>
          : <button className="btn-primary" data-testid="novo-grupo" onClick={() => setGrupoEditando("novo")}>+ Novo grupo</button>}
      />

      <div role="tablist" aria-label="Seções" className="mb-5 inline-flex rounded-xl bg-slate-100 p-1 text-sm">
        {([["usuarios", `Usuários${users.data ? ` (${users.data.filter((u) => u.active).length})` : ""}`],
           ["grupos", `Grupos${groups.data ? ` (${groups.data.length})` : ""}`]] as const).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={aba === k} onClick={() => setAba(k)}
            className={`rounded-lg px-4 py-1.5 font-medium ${aba === k ? "bg-white text-brand-800 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
            {l}
          </button>
        ))}
      </div>

      {aba === "usuarios" ? (
        users.isPending || groups.isPending ? <Spinner /> : users.error ? <ErrorBox error={users.error} /> : (
          <ListaUsuarios
            users={users.data ?? []}
            groups={groups.data ?? []}
            onEditar={setEditando}
            onConvite={setConvite}
            onExcluir={setExcluindo}
          />
        )
      ) : groups.isPending ? <Spinner /> : groups.error ? <ErrorBox error={groups.error} /> : (
        <ListaGrupos groups={groups.data ?? []} onEditar={setGrupoEditando} />
      )}

      {editando && groups.data && (
        <UsuarioModal
          user={editando === "novo" ? null : editando}
          groups={groups.data}
          onClose={() => setEditando(null)}
          onConvite={(c) => { setEditando(null); setConvite(c); }}
        />
      )}
      {grupoEditando && <GrupoModal group={grupoEditando === "novo" ? null : grupoEditando} onClose={() => setGrupoEditando(null)} />}
      {convite && <ConviteModal convite={convite} onClose={() => setConvite(null)} />}
      {excluindo && <ExcluirModal user={excluindo} onClose={() => setExcluindo(null)} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function ListaUsuarios({ users, groups, onEditar, onConvite, onExcluir }: {
  users: ManagedUser[];
  groups: PermissionGroup[];
  onEditar: (u: ManagedUser) => void;
  onConvite: (c: InviteShown) => void;
  onExcluir: (u: ManagedUser) => void;
}) {
  const { user: eu } = useAuth();
  const [busca, setBusca] = useState("");
  const [grupo, setGrupo] = useState("");
  const [inativos, setInativos] = useState(false);
  const gerar = useMutation({
    mutationFn: async (u: ManagedUser) => ({ user: u, ...((await api.post(`/admin/users/${u.id}/invite`)).data as { token: string; expiresAt: string }) }),
    onSuccess: onConvite,
  });

  const termo = busca.trim().toLowerCase();
  const lista = users.filter((u) =>
    (inativos || u.active) &&
    (!grupo || String(u.groupId ?? "") === grupo) &&
    (!termo || `${u.name} ${u.email} ${u.cargo ?? ""}`.toLowerCase().includes(termo)));

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input className="input max-w-xs" type="search" placeholder="Buscar por nome, e-mail ou cargo…" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar usuário" />
        <select className="input max-w-[14rem]" value={grupo} onChange={(e) => setGrupo(e.target.value)} aria-label="Filtrar por grupo">
          <option value="">Todos os grupos</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <label className="ml-1 inline-flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={inativos} onChange={(e) => setInativos(e.target.checked)} /> Mostrar inativos
        </label>
      </div>
      {gerar.error && <div className="mb-3"><ErrorBox error={gerar.error} /></div>}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="min-w-full text-sm" data-testid="tabela-usuarios">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Pessoa</th>
              <th className="px-4 py-3">Grupo</th>
              <th className="px-4 py-3">Situação</th>
              <th className="px-4 py-3">Último acesso</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lista.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">Nenhum usuário neste filtro.</td></tr>
            )}
            {lista.map((u) => {
              const s = situacao(u);
              const ajustes = u.grant.length + u.revoke.length;
              return (
                <tr key={u.id} className={u.active ? "" : "opacity-60"}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{u.name}{u.id === eu?.id && <span className="ml-2 text-xs font-normal text-slate-500">(você)</span>}</div>
                    <div className="text-xs text-slate-500">{u.email}{u.cargo ? ` · ${u.cargo}` : ""}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-slate-800">{u.groupName ?? "Sem grupo"}</span>
                    {ajustes > 0 && (
                      <span className="ml-2 rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-800 ring-1 ring-brand-200"
                        title={[...u.grant.map((p) => `+ ${LABEL[p]}`), ...u.revoke.map((p) => `− ${LABEL[p]}`)].join("\n")}>
                        {u.grant.length > 0 && `+${u.grant.length}`}{u.grant.length > 0 && u.revoke.length > 0 && " "}{u.revoke.length > 0 && `−${u.revoke.length}`} ajuste{ajustes > 1 ? "s" : ""}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ${SITUACAO_COR[s]}`}>{SITUACAO_LABEL[s]}</span>
                    {s === "convite" && u.inviteExpiresAt && <div className="mt-1 text-xs text-slate-500">vale até {dataHora(u.inviteExpiresAt)}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{dataHora(u.lastLoginAt)}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <button className="btn-ghost px-2 py-1 text-sm" onClick={() => onEditar(u)}>Editar</button>
                    {u.active && (
                      <button className="btn-ghost px-2 py-1 text-sm" disabled={gerar.isPending}
                        title="Gera um link novo para a pessoa definir a senha (o anterior deixa de valer)"
                        onClick={() => gerar.mutate(u)}>
                        {s === "convite" || s === "aguardando" ? "Reenviar convite" : "Redefinir senha"}
                      </button>
                    )}
                    {u.id !== eu?.id && (
                      <button className="btn-ghost px-2 py-1 text-sm text-red-700 hover:bg-red-50" onClick={() => onExcluir(u)}>Excluir</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function ListaGrupos({ groups, onEditar }: { groups: PermissionGroup[]; onEditar: (g: PermissionGroup) => void }) {
  const qc = useQueryClient();
  const excluir = useMutation({
    mutationFn: async (g: PermissionGroup) => api.delete(`/admin/groups/${g.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-groups"] }),
  });
  return (
    <div>
      {excluir.error && <div className="mb-3"><ErrorBox error={excluir.error} /></div>}
      <div className="grid gap-4 md:grid-cols-2" data-testid="lista-grupos">
        {groups.map((g) => (
          <div key={g.id} className="card flex flex-col p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">{g.name}</h3>
                <p className="mt-0.5 text-sm text-slate-500">{g.description || "Sem descrição"}</p>
              </div>
              <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-600">
                {g.userCount} {g.userCount === 1 ? "pessoa" : "pessoas"}
              </span>
            </div>
            <div className="mt-3 flex flex-1 flex-wrap content-start gap-1.5">
              {g.permissions.length === 0
                ? <span className="text-xs text-slate-400">Nenhuma permissão</span>
                : g.permissions.map((p) => (
                  <span key={p} className="rounded-md bg-brand-50 px-2 py-0.5 text-xs text-brand-800 ring-1 ring-brand-100">{LABEL[p] ?? p}</span>
                ))}
            </div>
            <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-3">
              {!g.system && g.userCount === 0 && (
                <button className="btn-ghost text-sm text-red-700 hover:bg-red-50" disabled={excluir.isPending}
                  onClick={() => { if (confirm(`Excluir o grupo "${g.name}"?`)) excluir.mutate(g); }}>Excluir</button>
              )}
              <button className="btn-secondary text-sm" onClick={() => onEditar(g)}>Editar permissões</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function ChecklistPermissoes({ marcadas, onChange, grupo, testId }: {
  marcadas: Permission[];
  onChange: (perm: Permission, marcado: boolean) => void;
  /** Permissões do grupo — quando presente, destaca o que é ajuste individual. */
  grupo?: Permission[];
  testId?: string;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid={testId}>
      {CATEGORIAS.map((cat) => (
        <fieldset key={cat.category} className="rounded-xl border border-slate-200 p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{cat.category}</legend>
          {cat.items.map((p) => {
            const key = p.key as Permission;
            const on = marcadas.includes(key);
            const ajuste = grupo ? on !== grupo.includes(key) : false;
            return (
              <label key={key} className={`flex cursor-pointer items-start gap-2.5 rounded-lg px-2 py-1.5 hover:bg-slate-50 ${ajuste ? "bg-brand-50/70" : ""}`}>
                <input type="checkbox" className="mt-0.5" checked={on} onChange={(e) => onChange(key, e.target.checked)} data-perm={key} />
                <span className="text-sm leading-snug">
                  <span className="font-medium text-slate-800">{p.label}</span>
                  {ajuste && <span className="ml-1.5 rounded bg-brand-600 px-1.5 py-px text-[10px] font-semibold uppercase text-white">{on ? "concedida" : "retirada"}</span>}
                  <span className="block text-xs text-slate-500">{p.description}</span>
                </span>
              </label>
            );
          })}
        </fieldset>
      ))}
    </div>
  );
}

function UsuarioModal({ user, groups, onClose, onConvite }: {
  user: ManagedUser | null;
  groups: PermissionGroup[];
  onClose: () => void;
  onConvite: (c: InviteShown) => void;
}) {
  const qc = useQueryClient();
  const padrao = groups.find((g) => g.slug === "analista") ?? groups[0];
  const [form, setForm] = useState({
    name: user?.name ?? "",
    email: user?.email ?? "",
    cargo: user?.cargo ?? "",
    groupId: user ? user.groupId : padrao?.id ?? null,
    active: user?.active ?? true,
  });
  const [ajustes, setAjustes] = useState<Ajustes>({ grant: user?.grant ?? [], revoke: user?.revoke ?? [] });
  const grupo = groups.find((g) => g.id === form.groupId) ?? null;
  const efetivas = useMemo(() => permissoesEfetivas(grupo?.permissions ?? null, ajustes), [grupo, ajustes]);

  const salvar = useMutation({
    mutationFn: async () => {
      const body = { ...form, ...ajustes };
      if (user) return { atualizado: (await api.put(`/admin/users/${user.id}`, body)).data as ManagedUser };
      return (await api.post("/admin/users", body)).data as { user: ManagedUser; invite: { token: string; expiresAt: string } };
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      qc.invalidateQueries({ queryKey: ["admin-groups"] });
      if ("invite" in r) onConvite({ user: r.user, token: r.invite.token, expiresAt: r.invite.expiresAt });
      else onClose();
    },
  });

  return (
    <Modal wide title={user ? `Editar ${user.name}` : "Novo usuário"} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); salvar.mutate(); }} data-testid="form-usuario">
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label className="label" htmlFor="u-nome">Nome</label><input id="u-nome" className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
          <div><label className="label" htmlFor="u-email">E-mail</label><input id="u-email" className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
          <div><label className="label" htmlFor="u-cargo">Cargo</label><input id="u-cargo" className="input" placeholder="Ex.: Coordenador de Logística" value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} /></div>
          <div>
            <label className="label" htmlFor="u-grupo">Grupo de permissão</label>
            <select id="u-grupo" className="input" value={form.groupId ?? ""} onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : null;
              const g = groups.find((x) => x.id === id);
              setForm({ ...form, groupId: id });
              setAjustes((a) => limparAjustes(g?.permissions ?? [], a));
            }}>
              <option value="">Sem grupo (só o que for marcado abaixo)</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
            {grupo?.description && <p className="mt-1 text-xs text-slate-500">{grupo.description}</p>}
          </div>
        </div>
        {user && (
          <label className="mt-4 inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Conta ativa (desmarcar corta o acesso na hora)
          </label>
        )}

        <div className="mb-2 mt-5 flex items-baseline justify-between gap-3">
          <h3 className="text-sm font-semibold text-slate-900">O que esta pessoa pode fazer</h3>
          {(ajustes.grant.length > 0 || ajustes.revoke.length > 0) && (
            <button type="button" className="text-xs font-semibold text-brand-700 underline" onClick={() => setAjustes({ grant: [], revoke: [] })}>
              Voltar ao padrão do grupo
            </button>
          )}
        </div>
        <p className="mb-3 text-xs text-slate-500">Vem do grupo. Marcar ou desmarcar aqui vale só para esta pessoa.</p>
        <ChecklistPermissoes
          testId="permissoes-usuario"
          marcadas={efetivas}
          grupo={grupo?.permissions ?? []}
          onChange={(p, on) => setAjustes((a) => alternarPermissao(grupo?.permissions ?? [], a, p, on))}
        />

        {!user && (
          <p className="mt-4 rounded-lg bg-gold-50 px-3 py-2 text-xs leading-relaxed text-gold-900 ring-1 ring-gold-200">
            Ninguém define senha por outra pessoa: ao criar, o portal gera um <b>link de convite</b> de uso único para
            você enviar no privado. A pessoa abre o link e escolhe a própria senha.
          </p>
        )}
        {salvar.error && <div className="mt-4"><ErrorBox error={salvar.error} /></div>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={salvar.isPending}>{user ? "Salvar" : "Criar e gerar convite"}</button>
        </div>
      </form>
    </Modal>
  );
}

function GrupoModal({ group, onClose }: { group: PermissionGroup | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(group?.name ?? "");
  const [description, setDescription] = useState(group?.description ?? "");
  const [perms, setPerms] = useState<Permission[]>(group?.permissions ?? ["paineis"]);
  const salvar = useMutation({
    mutationFn: async () => {
      const body = { name, description, permissions: perms };
      return group ? api.put(`/admin/groups/${group.id}`, body) : api.post("/admin/groups", body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-groups"] });
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      onClose();
    },
  });
  return (
    <Modal wide title={group ? `Grupo ${group.name}` : "Novo grupo"} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); salvar.mutate(); }} data-testid="form-grupo">
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label className="label" htmlFor="g-nome">Nome</label><input id="g-nome" className="input" value={name} onChange={(e) => setName(e.target.value)} required /></div>
          <div><label className="label" htmlFor="g-desc">Descrição</label><input id="g-desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
        </div>
        {group && group.userCount > 0 && (
          <p className="mt-3 text-xs text-slate-500">Vale para as {group.userCount} pessoa(s) do grupo assim que salvar (ajustes individuais continuam valendo).</p>
        )}
        <div className="mt-4">
          <ChecklistPermissoes testId="permissoes-grupo" marcadas={perms}
            onChange={(p, on) => setPerms((l) => on ? [...l, p] : l.filter((x) => x !== p))} />
        </div>
        {salvar.error && <div className="mt-4"><ErrorBox error={salvar.error} /></div>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={salvar.isPending}>Salvar grupo</button>
        </div>
      </form>
    </Modal>
  );
}

function ConviteModal({ convite, onClose }: { convite: InviteShown; onClose: () => void }) {
  const link = linkConvite(window.location.origin, convite.token);
  const texto = mensagemConvite(convite.user.name, link, convite.expiresAt);
  const [copiado, setCopiado] = useState<"" | "link" | "mensagem">("");
  const copiar = async (o: "link" | "mensagem") => {
    try { await navigator.clipboard.writeText(o === "link" ? link : texto); setCopiado(o); } catch { /* sem clipboard: o texto está visível para copiar à mão */ }
  };
  return (
    <Modal title={`Convite para ${convite.user.name}`} onClose={onClose}>
      <div data-testid="convite-gerado">
        <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm leading-relaxed text-red-800 ring-1 ring-red-200">
          <b>Envie só no privado</b> para {convite.user.name.split(" ")[0]} ({convite.user.email}) — nunca em grupo.
          Quem abrir o link primeiro define a senha. Ele vale uma vez, até {dataHora(convite.expiresAt)}, e
          <b> não será mostrado de novo</b> (se perder, gere outro).
        </div>
        <label className="label" htmlFor="convite-link">Link de convite</label>
        <div className="flex gap-2">
          <input id="convite-link" className="input font-mono text-xs" readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
          <button className="btn-secondary shrink-0" onClick={() => copiar("link")}>{copiado === "link" ? "Copiado ✓" : "Copiar link"}</button>
        </div>
        <label className="label mt-4" htmlFor="convite-msg">Mensagem pronta</label>
        <textarea id="convite-msg" className="input h-32 text-sm" readOnly value={texto} />
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button className="btn-secondary" onClick={() => copiar("mensagem")}>{copiado === "mensagem" ? "Mensagem copiada ✓" : "Copiar mensagem"}</button>
          <a className="btn-secondary" href={whatsappUrl(texto)} target="_blank" rel="noreferrer">Abrir no WhatsApp</a>
          <button className="btn-primary" onClick={onClose}>Concluir</button>
        </div>
      </div>
    </Modal>
  );
}

function ExcluirModal({ user, onClose }: { user: ManagedUser; onClose: () => void }) {
  const qc = useQueryClient();
  const excluir = useMutation({
    mutationFn: async () => (await api.delete(`/admin/users/${user.id}`)).data as { deleted: boolean },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-users"] }); qc.invalidateQueries({ queryKey: ["admin-groups"] }); onClose(); },
  });
  return (
    <Modal title={`Excluir ${user.name}?`} onClose={onClose}>
      <p className="text-sm leading-relaxed text-slate-700">
        A pessoa perde o acesso na hora. Se a conta já registrou ações no portal (vínculos, chamados), ela é
        <b> desativada</b> em vez de apagada, para o histórico continuar dizendo quem fez o quê.
      </p>
      {excluir.error && <div className="mt-4"><ErrorBox error={excluir.error} /></div>}
      <div className="mt-5 flex justify-end gap-2">
        <button className="btn-ghost" onClick={onClose}>Cancelar</button>
        <button className="btn-primary bg-red-600 hover:bg-red-700" data-testid="confirmar-exclusao" disabled={excluir.isPending} onClick={() => excluir.mutate()}>Excluir usuário</button>
      </div>
    </Modal>
  );
}
